import Container from "@/components/ContentContainer/Container";
import InnerContainer from "@/components/ContentContainer/InnerContainer";
import { Datasets } from "@/components/ImpactReferenceSelector/Datasets";
import ImpactReferenceSelector, {
  ImpactReferenceSelectedContentProps,
} from "@/components/ImpactReferenceSelector/ImpactReferenceSelector";
import Skeleton from "@/components/Skeleton/Skeleton";
import useAnalytics from "@/hooks/useAnalytics";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import useTranslation from "@/hooks/useTranslation";
import styles from "@/pages/products/detail/eol/eol.module.scss";
import { useDeleteEol, useGetEol, usePostEndOfLife } from "@/pages/products/eol/useEol";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Col, message, Row, Space } from "antd";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

function ProductEol() {
  const [selectedDataset, setSelectedDataset] = useState<ImpactDataset>();
  const { productId: id } = useParams();
  const productId = Number(id);

  const translation = useTranslation();
  const analytics = useAnalytics();

  const { data: companySettings, isLoading: companySettingsIsLoading } = useCompanySettings();
  const includeEol = companySettings?.include_eol || false;

  const { data, isLoading } = useGetEol({
    productId,
  });

  const deleteEndOfLife = useDeleteEol();
  const postEndOfLife = usePostEndOfLife();
  const onDelete = () => {
    return deleteEndOfLife
      .mutateAsync({
        productIds: [productId],
      })
      .then(() => {
        message.success(translation.eol.deletePopConfirm.successMessage);
        analytics.track("EOL reference deleted");
      })
      .catch(() => {
        message.error(translation.errors.general);
      });
  };
  const onSubmit = () => {
    if (selectedDataset) {
      return postEndOfLife
        .mutateAsync({
          productIds: [productId],
          match_id: selectedDataset.id,
        })
        .then(() => {
          message.success(translation.eol.impactModal.successMessage);
          analytics.track("EOL reference edited");
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    }
  };

  const selectedContent: ImpactReferenceSelectedContentProps = {
    type: "product",
    name: data?.name || "",
    infoMessage: translation.eol.impactModal.infoMessage,
  };

  const disabledContent = !!data?.proxy_id ? (
    <span>
      This is a proxy product and will inherit the end of life from its source product.{" "}
      <Link to={`/products/${data?.proxy_id}/eol`}>
        <span className={styles["proxyRedirectLink"]}>Go to its source</span>
      </Link>{" "}
      to edit the end of life.
    </span>
  ) : undefined;

  const onDatasetChange = (dataset?: ImpactDataset) => {
    setSelectedDataset(dataset);
  };

  const EolOffContent = (
    <Row gutter={[0, 20]}>
      <Col
        span={24}
        className={styles.eolContentContainer}
      >
        {!companySettingsIsLoading ? (
          <div style={{ padding: 16, borderRadius: 16, border: "1px solid #D9D9D9" }}>
            <Row>
              <Col span={12}>{translation.product.eol.outOfScope}</Col>
              <Col
                span={12}
                style={{ textAlign: "right" }}
              >
                <Link to={"/settings"}>{translation.product.eol.manageScope}</Link>
              </Col>
            </Row>
          </div>
        ) : (
          <Skeleton style={{ height: 70, width: "100%" }} />
        )}
      </Col>
    </Row>
  );

  const EolOnContent = (
    <Row>
      <Col
        span={24}
        className={styles.eolContentContainer}
      >
        <ImpactReferenceSelector
          confirmedDataset={data?.match}
          selectedDataset={selectedDataset}
          onDatasetChange={onDatasetChange}
          loading={{
            onDatasetConfirm: postEndOfLife.isLoading,
            data: isLoading,
          }}
          defaultDatabaseSearch={{ sector: "Waste Treatment & Recycling" }}
          modalTitle={translation.product.eol.ReferenceSelectorTitle}
          showIncomplete={true}
          onDatasetConfirm={onSubmit}
          onDelete={onDelete}
          selectedContent={selectedContent}
          disabledTooltip={!!data?.proxy_id ? disabledContent : undefined}
          defaultTab="suggestions"
          suggestionTabContent={
            <Datasets
              key="EOL"
              selectedDataset={selectedDataset}
              onDatasetChange={onDatasetChange}
              datasetType="eol"
            />
          }
        />
      </Col>
    </Row>
  );

  return (
    <Container
      padding={false}
      noRoundedTop
    >
      <InnerContainer title={translation.product.eol.heading}>
        <Row gutter={[10, 20]}>
          <Col span={24}>{translation.product.eol.description}</Col>
          <Col span={24}>{!includeEol ? EolOffContent : EolOnContent}</Col>
          <Col span={24}>
            <Space>
              <InfoCircleOutlined className={styles.infoIcon} />
              <span className={styles.infoMessage}>{translation.product.eol.infoMessage}</span>
            </Space>
          </Col>
        </Row>
      </InnerContainer>
    </Container>
  );
}

export default ProductEol;
